<template>
    <Card class="p-4 mb-4" :header="$t('pluginCampaignsList.title')" :loading="loading && !filteredAvailableCampaigns.length">
        <div class="d-flex gap-3 mb-4">
            <ButtonIcon
                icon="file-text"
                :icon-size="24"
                :href="$t('pluginCode.customizationLink')"
                target="_blank"
                variant="link"
                class="p-0"
            >
                {{ $t('pluginCode.seeHowToCustomizePlugin') }}
            </ButtonIcon>
        </div>

        <ErrorMessage :value="error" class="mb-4" />

        <div class="d-flex flex-column">
            <div class="d-flex gap-5 flex-wrap">
                <div class="d-flex flex-column flex-grow-1 flex-basis-0">
                    <p>{{ $t('pluginCampaignsList.availableCampaigns') }}</p>
                    <p class="mb-4 small mt-n3">
                        {{ $t('pluginCampaignsList.availableCampaignsDescription') }}
                    </p>

                    <div class="d-flex flex-column gap-3 max-h p-4">
                        <div v-for="campaign in filteredAvailableCampaigns" :key="campaign.id" class="d-flex align-items-center border-bottom py-2 gap-3">
                            <span>{{ campaign.name }}</span>
                            <ButtonIcon hide-label-while-loading variant="success" class="ml-auto" :loading="createSubmitting.includes(campaign.id)" @click="addCampaign(campaign)">
                                {{ $t('add') }}
                            </ButtonIcon>
                        </div>

                        <div v-if="!filteredAvailableCampaigns.length" class="text-center py-3 text-muted">
                            {{ $t('pluginCampaignsList.noAvailableCampaigns') }}
                        </div>
                    </div>
                </div>

                <div class="d-flex flex-column flex-grow-1 flex-basis-0">
                    <p>{{ $t('pluginCampaignsList.activeCampaigns') }}</p>
                    <p class="mb-4 small mt-n3">
                        {{ $t('pluginCampaignsList.activeCampaignsDescription') }}
                    </p>

                    <div class="d-flex flex-column gap-3 max-h p-4">
                        <div v-for="campaign in filteredActiveCampaigns" :key="campaign.campaign.id" class="d-flex align-items-center border-bottom py-2 gap-3">
                            <span class="flex-grow-1">{{ campaign.campaign.name }}</span>

                            <b-form-input
                                v-model.number="campaign.priority"
                                type="number"
                                min="0"
                                class="w-25"
                                :disabled="isDeleting(campaign.campaign.id) || isUpdating(campaign.campaign.id)"
                                @input="debouncedUpdateCampaign(campaign)"
                                @blur="updateCampaign(campaign)"
                            />

                            <ButtonIcon
                                hide-label-while-loading
                                variant="danger"
                                class="ml-3"
                                :loading="isDeleting(campaign.campaign.id)"
                                :disabled="isUpdating(campaign.campaign.id) || isDeleting(campaign.campaign.id)"
                                @click="removeCampaign(campaign)"
                            >
                                {{ $t('remove') }}
                            </ButtonIcon>
                        </div>

                        <div v-if="!filteredActiveCampaigns.length" class="text-center py-3 text-muted">
                            {{ $t('pluginCampaignsList.noActiveCampaigns') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Card>
</template>

<script>
    import ButtonIcon from '@/components/common/ButtonIcon.vue';
    import pluginService from '@/services/pluginService';
    import ErrorMessage from '@/components/common/ErrorMessage.vue';

    export default {
        name: 'PluginCampaignsList',
        components: {
            ButtonIcon,
            ErrorMessage,
        },
        props: {
            plugin: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                activeCampaigns: [],
                availableCampaigns: [],

                error: null,
                loading: false,

                createSubmitting: [],
                deleteSubmitting: [],
                updateSubmitting: [],
                updateTimeouts: {},
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },

            filteredAvailableCampaigns() {
                const campaignsArray = Object.values(this.availableCampaigns || {});
                return campaignsArray.filter(item => !this.filteredActiveCampaigns.some(active => active.campaign.id === item.id));
            },

            filteredActiveCampaigns() {
                const campaignsArray = Object.values(this.activeCampaigns || {});
                return campaignsArray;
            },
        },
        methods: {
            async getData() {
                this.loading = true;
                try {
                    await Promise.all([this.getActiveCampaigns(), this.getAvailableCampaigns()]);
                } finally {
                    this.loading = false;
                }
            },

            isUpdating(id) {
                return this.updateSubmitting.includes(id);
            },

            isDeleting(id) {
                return this.deleteSubmitting.includes(id);
            },

            async getActiveCampaigns() {
                try {
                    this.error = null;
                    const campaigns = await pluginService.fetchActiveCampaigns(this.plugin.id);
                    // Sort by priority descending (highest weight first), then by name
                    this.activeCampaigns = campaigns.sort((a, b) => {
                        const priorityA = a.priority || 0;
                        const priorityB = b.priority || 0;
                        if (priorityA !== priorityB) {
                            return priorityB - priorityA;
                        }
                        return a.campaign.name.localeCompare(b.campaign.name);
                    });
                } catch (e) {
                    this.error = e.message;
                }
            },

            async getAvailableCampaigns() {
                try {
                    this.error = null;
                    this.availableCampaigns = await pluginService.fetchAvailableCampaigns(this.plugin.id);
                } catch (e) {
                    this.error = e.message;
                }
            },

            async addCampaign(campaign) {
                this.error = null;
                this.createSubmitting.push(campaign.id);
                try {
                    await pluginService.createCampaign(this.plugin.id, campaign.id);
                    await this.getData();
                } catch (e) {
                    this.error = e.message;
                } finally {
                    this.createSubmitting = this.createSubmitting.filter(id => id !== campaign.id);
                }
            },

            async removeCampaign(campaign) {
                this.error = null;
                const id = campaign.campaign.id;
                this.deleteSubmitting.push(id);
                try {
                    await pluginService.deleteCampaign(this.plugin.id, id);
                    await this.getData();
                } catch (e) {
                    this.error = e.message;
                } finally {
                    this.deleteSubmitting = this.deleteSubmitting.filter(x => x !== id);
                }
            },

            debouncedUpdateCampaign(campaign) {
                if (this.updateTimeouts && this.updateTimeouts[campaign.campaign.id]) {
                    clearTimeout(this.updateTimeouts[campaign.campaign.id]);
                }

                if (!this.updateTimeouts) {
                    this.updateTimeouts = {};
                }

                this.updateTimeouts[campaign.campaign.id] = setTimeout(() => {
                    this.updateCampaign(campaign);
                }, 800);
            },

            async updateCampaign(campaign) {
                this.error = null;
                const id = campaign.campaign.id;

                if (this.updateTimeouts && this.updateTimeouts[id]) {
                    clearTimeout(this.updateTimeouts[id]);
                    delete this.updateTimeouts[id];
                }

                this.updateSubmitting.push(id);
                try {
                    await pluginService.updateCampaign(this.plugin.id, id, {
                        priority: campaign.priority || 0,
                    });

                    await this.getActiveCampaigns();
                } catch (e) {
                    this.error = e.message;
                } finally {
                    this.updateSubmitting = this.updateSubmitting.filter(x => x !== id);
                }
            },
        },
        mounted() {
            this.getData();
        },

        beforeDestroy() {
            if (this.updateTimeouts) {
                Object.values(this.updateTimeouts).forEach(timeout => {
                    if (timeout) clearTimeout(timeout);
                });
            }
        },
    };
</script>

<style scoped>
    .w-25 {
        width: 25% !important;
    }

    .max-h {
        max-height: 1300px;
        overflow-y: auto;
    }
</style>
